import {
  EntityTypeEnum,
  InterviewRequestSourceEnum,
  OperationTypeEnum
} from "@rooster/types";
import { baseUrlServer } from "./common";
import { axiosWithRetry } from "./utils/axiosRetry";

export interface AcceptedTermPayload {
  type: "Candidate" | "Rooster";
  termsAndConditionId: string;
  version: number;
  contentId: string;
  localeCode: string;
}

export interface RecordTermsAcceptancePayload {
  clientId: string;
  requestType: InterviewRequestSourceEnum;
  operationType: OperationTypeEnum;
  entityType: EntityTypeEnum;
  entityId: string;
  meetingLinkId?: string;
  trackingId: string;
  acceptedTerms: AcceptedTermPayload[];
}

/**
 * Awaited best-effort write. Resolves even on failure — callers must not
 * surface a T&C failure to the candidate, but they DO sequence this call
 * after the schedule API succeeds so entityId is known.
 */
export const recordTermsAcceptance = async (
  payload: RecordTermsAcceptancePayload
): Promise<void> => {
  // Guard client-side invariants before firing. The server's Joi validator
  // would reject these with a 400 which, under best-effort semantics,
  // would still leave a compliance-relevant gap in the audit log.
  if (!payload.clientId) {
    console.error(
      "[recordTermsAcceptance] missing clientId — skipping acceptance record"
    );
    return;
  }
  if (!payload.entityId) {
    console.error(
      "[recordTermsAcceptance] missing entityId — skipping acceptance record"
    );
    return;
  }
  if (!payload.acceptedTerms || payload.acceptedTerms.length === 0) {
    console.error(
      "[recordTermsAcceptance] acceptedTerms is empty — skipping acceptance record"
    );
    return;
  }

  const url = `${baseUrlServer}/settings/termsAndConditions/acceptance`;
  // maxRetries: 0 — a compliance write must not silently duplicate on a
  // transient server blip. The caller has already awaited; the schedule
  // succeeded; we accept loss over double-write.
  try {
    await axiosWithRetry(
      url,
      "post",
      { data: payload, timeout: 10000 },
      { maxRetries: 0 }
    );
  } catch (err: unknown) {
    console.error("[recordTermsAcceptance] failed", err);
  }
};
